import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Flex,
  Heading,
  Button,
  Text,
  Box,
  SimpleGrid,
  Grid,
  GridItem,
  List,
  ListItem,
  IconButton,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { ArrowBackIcon, CheckIcon } from '@chakra-ui/icons';
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { TEMPLATE_PRESETS } from '../../../../constants/templatePresets';
import {
  ALL_CATEGORY,
  PRESET_CATEGORIES,
  GalleryCategory,
  filterPresetsByCategory,
  getRecommendedPresetIds,
  sortRecommendedFirst,
} from '../../../../constants/templatePresets/gallery';
import { Industry, UseCase } from '../../../../types/onboarding';
import { TemplatePreset, PreviewData } from '../../../../store/types/template';
import { MeContext } from '../../../../context/MeContext';
import { buildPreviewDataFromPreset } from '../utils/templatePreviewData';
import ApplePassFront from '../previews/apple/ApplePassFront';
import GooglePassFront from '../previews/google/GooglePassFront';
import GoogleExpressivePassPreview from '../previews/google/GoogleExpressivePassPreview';
import PresetDetailScrollButton from './PresetDetailScrollButton';
import {
  ScaledPassPreview,
  ScaledPassPreviewByHeight,
  APPLE_PREVIEW_DESIGN_WIDTH,
  GOOGLE_PREVIEW_DESIGN_WIDTH,
} from '../previews/ScaledPassPreview';

interface PrebuiltTemplatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPreset: (presetId: string) => void;
  onSelectBlank: () => void;
}

const PresetGridCard = ({
  preset,
  previewData,
  recommended,
  onClick,
}: {
  preset: TemplatePreset;
  previewData: PreviewData;
  recommended: boolean;
  onClick: () => void;
}) => (
  <Flex
    as="button"
    type="button"
    role="group"
    onClick={onClick}
    aria-label={`View the ${preset.metadata.name} preset`}
    data-preset-id={preset.metadata.id}
    direction="column"
    textAlign="left"
    borderRadius="lg"
    p={6}
    gap={1}
    bg="white"
    layerStyle="hoverCard"
  >
    <Box
      alignSelf="flex-start"
      bg="recommendedGreenLight"
      color="recommendedGreen"
      fontSize="12px"
      fontWeight="600"
      lineHeight="1.3"
      px="4px"
      py="2px"
      borderRadius="3px"
      mb={2}
      visibility={recommended ? 'inherit' : 'hidden'}
    >
      Recommended
    </Box>
    <Box w="100%" mb={3}>
      <ScaledPassPreview>
        <ApplePassFront
          previewData={previewData}
          templatesPage
          notifOpen={false}
          cutoutBg="white"
        />
      </ScaledPassPreview>
    </Box>
    <Flex justify="space-between" align="center">
      <Heading size="sm">{preset.metadata.name}</Heading>
      <Box
        as="svg"
        width="20px"
        height="15.7px"
        viewBox="0 0 15.3 12"
        fill="none"
        flexShrink={0}
        stroke="subheadingGrey"
        _groupHover={{ stroke: 'url(#brandGradientVertical)' }}
        aria-hidden="true"
      >
        <path
          d="M1 6H14.3M9.3 1.5L14.3 6L9.3 10.5"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Box>
    </Flex>
  </Flex>
);

const PresetDetail = ({
  preset,
  previewData,
  expressivePassRolloutEnabled,
}: {
  preset: TemplatePreset;
  previewData: PreviewData;
  expressivePassRolloutEnabled: boolean;
}) => (
  <Box>
    <Flex justify="center" gap={20} wrap="wrap" mb={12} mt={5} align="stretch">
      <Flex direction="column" align="center" gap={3}>
        <Text textStyle="bodyFamilySemiBold" fontSize="sm" color="inkBlue">
          Apple pass
        </Text>
        <Flex align="center" justify="center" flex={1}>
          <ScaledPassPreviewByHeight
            targetHeight={previewData.passType === 'transit' ? 350 : 385}
            designWidth={APPLE_PREVIEW_DESIGN_WIDTH}
          >
            <ApplePassFront
              previewData={previewData}
              templatesPage
              createTemplate
              notifOpen={false}
            />
          </ScaledPassPreviewByHeight>
        </Flex>
      </Flex>
      <Flex direction="column" align="center" gap={3}>
        <Text textStyle="bodyFamilySemiBold" fontSize="sm" color="inkBlue">
          Google pass
        </Text>
        <Flex align="center" justify="center" flex={1}>
          {expressivePassRolloutEnabled &&
          previewData.passType !== 'transit' ? (
            <Box
              overflow="hidden"
              h="437px"
              borderTopRadius="25px"
              borderBottomRadius="0"
              w="242px"
              pointerEvents="none"
            >
              <Box
                transform="scale(0.7125)"
                transformOrigin="top left"
                w={`${100 / 0.7125}%`}
              >
                <GoogleExpressivePassPreview
                  previewData={previewData}
                  templatesPage
                  createTemplate
                  notifOpen={false}
                />
              </Box>
            </Box>
          ) : (
            <ScaledPassPreviewByHeight
              targetHeight={350}
              designWidth={GOOGLE_PREVIEW_DESIGN_WIDTH}
            >
              <GooglePassFront
                previewData={previewData}
                templatesPage
                createTemplate
                notifOpen={false}
              />
            </ScaledPassPreviewByHeight>
          )}
        </Flex>
      </Flex>
    </Flex>

    <Box mb={10}>
      <Heading size="sm" mb={2}>
        Description
      </Heading>
      <Text>{preset.metadata.description}</Text>
    </Box>

    <Box mb={10}>
      <Heading size="sm" mb={2}>
        Best for:
      </Heading>
      <List spacing={1} styleType="disc" pl={5}>
        {preset.metadata.bestFor.map((item) => (
          <ListItem key={item}>{item}</ListItem>
        ))}
      </List>
    </Box>

    <Box>
      <Heading size="sm" mb={2}>
        Preset features:
      </Heading>
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacingY={1} spacingX={6}>
        {preset.metadata.features.map((feature) => (
          <Flex key={feature} align="center" gap={2}>
            <Icon as={CheckIcon} fontSize="12px" />
            <Text>{feature}</Text>
          </Flex>
        ))}
      </SimpleGrid>
    </Box>
  </Box>
);

const PrebuiltTemplatePickerModal = ({
  isOpen,
  onClose,
  onSelectPreset,
  onSelectBlank,
}: PrebuiltTemplatePickerModalProps) => {
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] =
    useState<GalleryCategory>(ALL_CATEGORY);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const returnedFromDetailAtRef = useRef(0);
  const returnToPresetIdRef = useRef<string | null>(null);
  const detailRef = useRef<HTMLDivElement | null>(null);
  const [detailHasMoreBelow, setDetailHasMoreBelow] = useState(false);
  const [detailBounceCount, setDetailBounceCount] = useState(0);

  const updateDetailFade = () => {
    const el = detailRef.current;
    setDetailHasMoreBelow(
      el ? el.scrollHeight - el.scrollTop - el.clientHeight > 8 : false
    );
  };

  useLayoutEffect(() => {
    if (selectedPresetId || !returnToPresetIdRef.current) return undefined;
    const grid = gridRef.current;
    if (!grid) return undefined;
    const scrollTop = grid.scrollTop;
    const card = grid.querySelector<HTMLButtonElement>(
      `[data-preset-id="${returnToPresetIdRef.current}"]`
    );
    returnToPresetIdRef.current = null;
    card?.focus({ preventScroll: true });
    const frame = requestAnimationFrame(() => {
      grid.scrollTop = scrollTop;
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedPresetId]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedPresetId(null);
      setActiveCategory(ALL_CATEGORY);
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    const el = detailRef.current;
    if (!selectedPresetId || !el) {
      setDetailHasMoreBelow(false);
      return undefined;
    }
    updateDetailFade();
    setDetailBounceCount((count) => count + 1);
    const observer = new ResizeObserver(updateDetailFade);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [selectedPresetId]);

  const previewDataByPreset = useMemo(
    () =>
      Object.fromEntries(
        TEMPLATE_PRESETS.map((preset) => [
          preset.metadata.id,
          buildPreviewDataFromPreset(preset),
        ])
      ),
    []
  );

  const selectedPreset = selectedPresetId
    ? TEMPLATE_PRESETS.find((p) => p.metadata.id === selectedPresetId)
    : undefined;

  const meCtx = useContext(MeContext) as {
    state?: {
      organisationStatus?: string;
      expressivePassRolloutEnabled?: boolean;
      primaryUseCase?: UseCase[] | null;
      industry?: Industry | null;
    };
  };
  const expressivePassRolloutEnabled =
    meCtx?.state?.expressivePassRolloutEnabled === true;
  const primaryUseCase = meCtx?.state?.primaryUseCase ?? null;
  const industry = meCtx?.state?.industry ?? null;

  const recommendedPresetIds = useMemo(
    () => getRecommendedPresetIds(TEMPLATE_PRESETS, primaryUseCase, industry),
    [primaryUseCase, industry]
  );
  const visiblePresets = useMemo(
    () =>
      sortRecommendedFirst(
        filterPresetsByCategory(TEMPLATE_PRESETS, activeCategory),
        recommendedPresetIds
      ),
    [activeCategory, recommendedPresetIds]
  );
  const previewMeCtx = {
    ...meCtx,
    state: { ...meCtx?.state, organisationStatus: 'active' },
  };

  return (
    <MeContext.Provider value={previewMeCtx as never}>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="6xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent
          bg="softWhite"
          borderRadius="2xl"
          overflow="hidden"
          p={0}
          h="720px"
          maxH="85vh"
        >
          <ModalCloseButton />
          <ModalBody p={0} overflow="hidden" display="flex" minH={0}>
            <svg
              width="0"
              height="0"
              style={{ position: 'absolute' }}
              aria-hidden="true"
            >
              <defs>
                <linearGradient
                  id="brandGradientVertical"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="16%" stopColor="#FF9554" />
                  <stop offset="100%" stopColor="#FF4935" />
                </linearGradient>
              </defs>
            </svg>
            <Grid
              templateAreas={{
                base: `"header" "divider" "nav" "content"`,
                md: `". header" "divider divider" "nav content"`,
              }}
              templateColumns={{ base: '1fr', md: '200px 1fr' }}
              templateRows={{
                base: 'auto auto auto 1fr',
                md: 'auto auto 1fr',
              }}
              w="100%"
              minH={0}
            >
              <GridItem area="header" px={6} pt={6} minW={0}>
                <Text
                  textStyle="heading3"
                  mb={2}
                  color="inkBlue"
                  fontWeight="400"
                >
                  Choose a preset
                </Text>
                <Text textStyle="bodySmall" fontWeight="400">
                  Every preset is a starting point – colours, images and fields
                  are all yours to change. Or start from scratch.
                </Text>
              </GridItem>
              <GridItem area="divider">
                <Divider border="0.5px solid lightGrey" mt="15px" mb={0} />
              </GridItem>
              <GridItem
                area="nav"
                as="nav"
                aria-label="Preset categories"
                display="flex"
                flexDirection={{ base: 'row', md: 'column' }}
                flexWrap={{ base: 'wrap', md: 'nowrap' }}
                borderRightWidth={{ base: 0, md: '1px' }}
                borderBottomWidth={{ base: '1px', md: 0 }}
                borderStyle="solid"
                borderColor="borderGrey"
                alignContent="flex-start"
              >
                {[ALL_CATEGORY, ...PRESET_CATEGORIES].map((category) => (
                  <Box
                    key={category}
                    as="button"
                    type="button"
                    onClick={() => {
                      setActiveCategory(category);
                      setSelectedPresetId(null);
                      if (gridRef.current) {
                        gridRef.current.scrollTop = 0;
                      }
                    }}
                    aria-current={activeCategory === category}
                    textAlign="left"
                    w={{ base: 'auto', md: '100%' }}
                    px={4}
                    py={3}
                    fontSize="sm"
                    fontWeight={activeCategory === category ? '600' : '500'}
                    color={activeCategory === category ? 'inkBlue' : 'grey'}
                    bg={
                      activeCategory === category
                        ? 'lightestGrey'
                        : 'transparent'
                    }
                    _hover={{ color: 'inkBlue' }}
                  >
                    {category}
                  </Box>
                ))}
              </GridItem>
              {selectedPreset && (
                <GridItem
                  ref={detailRef}
                  area="content"
                  px={6}
                  pt={4}
                  pb={8}
                  minW={0}
                  minH={0}
                  overflowY="auto"
                  onScroll={updateDetailFade}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    gap={4}
                    mt={4}
                    mb={8}
                  >
                    <Flex align="center" gap={2} minW={0}>
                      <IconButton
                        aria-label="Back"
                        icon={<ArrowBackIcon boxSize={6} />}
                        variant="ghost"
                        size="sm"
                        color="grey"
                        _hover={{ color: 'orangePrimary' }}
                        onClick={() => {
                          returnedFromDetailAtRef.current = Date.now();
                          returnToPresetIdRef.current =
                            selectedPreset.metadata.id;
                          setSelectedPresetId(null);
                        }}
                      />
                      <Text textStyle="heading4" fontWeight="400" noOfLines={1}>
                        {selectedPreset.metadata.name}
                      </Text>
                    </Flex>
                    <Button
                      onClick={() => onSelectPreset(selectedPreset.metadata.id)}
                      flexShrink={0}
                    >
                      Use this preset
                    </Button>
                  </Flex>
                  <PresetDetail
                    preset={selectedPreset}
                    previewData={
                      previewDataByPreset[selectedPreset.metadata.id]
                    }
                    expressivePassRolloutEnabled={expressivePassRolloutEnabled}
                  />
                </GridItem>
              )}
              {selectedPreset && (
                <Box
                  gridArea="content"
                  alignSelf="end"
                  justifySelf="end"
                  mr={8}
                  mb={6}
                  zIndex={1}
                >
                  <PresetDetailScrollButton
                    scrollContainerRef={detailRef}
                    isAtBottom={!detailHasMoreBelow}
                    bounceTrigger={detailBounceCount}
                  />
                </Box>
              )}
              <SimpleGrid
                ref={gridRef}
                gridArea="content"
                visibility={selectedPreset ? 'hidden' : 'visible'}
                columns={{ base: 1, sm: 2, md: 3 }}
                spacing={8}
                w="100%"
                minW={0}
                minH={0}
                overflowY="auto"
                alignContent="flex-start"
                px={6}
                pt={6}
                pb={8}
              >
                <Flex
                  as="button"
                  type="button"
                  role="group"
                  onClick={() => {
                    if (Date.now() - returnedFromDetailAtRef.current < 400) {
                      return;
                    }
                    onSelectBlank();
                  }}
                  aria-label="Start from blank"
                  direction="column"
                  align="center"
                  justify="center"
                  gap={1}
                  w="100%"
                  h="100%"
                  minH={visiblePresets.length === 0 ? '325px' : undefined}
                  p={6}
                  mb={6}
                  borderRadius="lg"
                  layerStyle="hoverCard"
                  bg="white"
                >
                  <Flex
                    position="relative"
                    align="center"
                    justify="center"
                    gap={2}
                    direction="column"
                    bg="softWhite"
                    borderRadius="3xl"
                    p={4}
                    w="100%"
                    h="100%"
                    stroke="lightGrey"
                    _groupHover={{ stroke: 'url(#brandGradientVertical)' }}
                  >
                    <Box
                      as="svg"
                      position="absolute"
                      inset={0}
                      w="100%"
                      h="100%"
                      fill="none"
                      overflow="visible"
                      aria-hidden="true"
                    >
                      <rect
                        x="0"
                        y="0"
                        width="100%"
                        height="100%"
                        rx="22"
                        ry="22"
                        strokeWidth="1.5"
                        strokeDasharray="5 5"
                      />
                    </Box>
                    <Box
                      as="svg"
                      width="35px"
                      height="35px"
                      viewBox="0 0 35 35"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle cx="17.5" cy="17.5" r="16.75" strokeWidth="1.5" />
                      <path
                        d="M17.5 11.5V23.5M11.5 17.5H23.5"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                      />
                    </Box>
                    <Text
                      color="grey"
                      _groupHover={{
                        bgGradient: 'linear(to-b, #FF9554 16%, #FF4935)',
                        bgClip: 'text',
                        color: 'transparent',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Blank template
                    </Text>
                  </Flex>
                </Flex>
                {visiblePresets.map((preset) => (
                  <PresetGridCard
                    key={preset.metadata.id}
                    preset={preset}
                    previewData={previewDataByPreset[preset.metadata.id]}
                    recommended={recommendedPresetIds.has(preset.metadata.id)}
                    onClick={() => setSelectedPresetId(preset.metadata.id)}
                  />
                ))}
              </SimpleGrid>
            </Grid>
          </ModalBody>
        </ModalContent>
      </Modal>
    </MeContext.Provider>
  );
};

export default PrebuiltTemplatePickerModal;
